/**
 * MFA Settings Component
 *
 * Allows users to:
 * - Enable/disable TOTP MFA
 * - View and regenerate backup codes
 * - Check MFA status
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  AlertCircle,
  CheckCircle,
  Key,
  Download,
  RefreshCw,
  Lock,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import MFAEnrollment from '../auth/MFAEnrollment';
import analyticsService from '../../services/analytics';

interface MFAStatus {
  enabled: boolean;
  enrolled_at: string | null;
  last_used: string | null;
}

interface BackupCodesData {
  codes: string[];
  generated_at: string;
  remaining_count: number;
}

export default function MFASettings() {
  const { getAuthToken } = useAuth();
  const [status, setStatus] = useState<MFAStatus | null>(null);
  // Two-step confirm for a destructive action. See the button below.
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // MFA enrollment
  const [showEnrollment, setShowEnrollment] = useState(false);

  // Backup codes
  const [backupCodes, setBackupCodes] = useState<BackupCodesData | null>(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // Disable MFA
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling, setDisabling] = useState(false);

  // Backup codes generation loading state
  const [generatingBackupCodes, setGeneratingBackupCodes] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || '';

  const fetchMFAStatus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_URL}/api/v1/mfa/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch MFA status');
      }

      const data = await response.json();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, API_URL]);

  useEffect(() => {
    fetchMFAStatus();
  }, [fetchMFAStatus]);

  const handleEnrollmentComplete = () => {
    setShowEnrollment(false);
    fetchMFAStatus();
    analyticsService.trackMfaEnabled();
  };

  const handleGenerateBackupCodes = async () => {
    if (generatingBackupCodes) return; // Prevent double-clicks

    setGeneratingBackupCodes(true);
    setError(null);

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_URL}/api/v1/mfa/backup-codes/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to generate backup codes');
      }

      const data: BackupCodesData = await response.json();
      setBackupCodes(data);
      setShowBackupCodes(true);
      analyticsService.trackEvent({
        name: 'mfa_backup_codes_generated',
        category: 'mfa',
        action: 'backup_codes_generated',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate backup codes');
    } finally {
      setGeneratingBackupCodes(false);
    }
  };

  const handleDownloadBackupCodes = () => {
    if (!backupCodes) return;

    // Show security warning
    if (!confirm('WARNING: This file contains sensitive security codes.\n\nSave it in a secure location (password manager, encrypted drive).\nDo not share these codes with anyone.\n\nProceed with download?')) {
      return;
    }

    const text = `ContentSage MFA Backup Codes
Generated: ${new Date(backupCodes.generated_at).toLocaleString()}

SECURITY WARNING
These codes provide access to your account.
Store them securely and never share them.
Each code can only be used once.

${backupCodes.codes.join('\n')}

Keep these codes safe and secure!
`;

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contentsage-backup-codes-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    analyticsService.trackEvent({
      name: 'mfa_backup_codes_downloaded',
      category: 'mfa',
      action: 'backup_codes_downloaded',
    });
  };

  const handleDisableMFA = async () => {
    if (!disableCode || !disablePassword) {
      setError('Please enter both TOTP code and password');
      return;
    }

    // Validate TOTP code format (6 digits) or backup code (8+ alphanumeric)
    const is6DigitTOTP = /^\d{6}$/.test(disableCode);
    const isBackupCode = /^[A-Za-z0-9]{8,}$/.test(disableCode);

    if (!is6DigitTOTP && !isBackupCode) {
      setError('Invalid code format. Enter a 6-digit TOTP code or backup code.');
      return;
    }

    setDisabling(true);
    setError(null);

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_URL}/api/v1/mfa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: disableCode,
          password: disablePassword,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.verified) {
        throw new Error(data.message || data.detail || 'Failed to disable MFA');
      }

      setShowDisableModal(false);
      setDisableCode('');
      setDisablePassword('');
      fetchMFAStatus();
      analyticsService.trackMfaDisabled();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable MFA');
      // Clear sensitive data immediately
      setDisablePassword('');
    } finally {
      setDisabling(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* MFA Status Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              status?.enabled ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <Shield className={`w-6 h-6 ${
                status?.enabled ? 'text-green-600' : 'text-gray-400'
              }`} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Two-Factor Authentication
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {status?.enabled
                  ? 'Your account is protected with 2FA'
                  : 'Add an extra layer of security to your account'}
              </p>
              {status?.enabled && status.enrolled_at && (
                <p className="text-xs text-gray-500 mt-2">
                  Enabled on {new Date(status.enrolled_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {status?.enabled ? (
              <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Enabled
              </span>
            ) : (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                Disabled
              </span>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </motion.div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          {status?.enabled ? (
            <>
              {/*
                This button does NOT view anything - it POSTs to
                /mfa/backup-codes/generate, which the backend documents as
                "WARNING: This replaces any existing backup codes!". Labelled
                "View Backup Codes", it invited a user with three codes left to
                destroy all three while believing they were reading them.
                Recovery codes are the last way back into an enforced account,
                so the label now says what happens and a second click confirms
                it. MFAStatus carries no remaining-code count, so the warning
                cannot be shown conditionally - it is always shown.
              */}
              {confirmingRegenerate ? (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-amber-800">
                    This replaces any codes you already have. Unused ones stop working.
                  </span>
                  <button
                    onClick={() => {
                      setConfirmingRegenerate(false);
                      handleGenerateBackupCodes();
                    }}
                    disabled={generatingBackupCodes}
                    className="px-4 py-2 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition disabled:opacity-50"
                  >
                    {generatingBackupCodes ? 'Generating...' : 'Replace my codes'}
                  </button>
                  <button
                    onClick={() => setConfirmingRegenerate(false)}
                    className="px-4 py-2 text-gray-600 font-medium hover:text-gray-900 transition"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmingRegenerate(true)}
                  disabled={generatingBackupCodes}
                  className="px-4 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition flex items-center gap-2 disabled:opacity-50"
                >
                  <Key className="w-4 h-4" />
                  Regenerate Backup Codes
                </button>
              )}
              <button
                onClick={() => setShowDisableModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Disable 2FA
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowEnrollment(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition flex items-center gap-2"
            >
              <Shield className="w-4 h-4" />
              Enable Two-Factor Auth
            </button>
          )}
        </div>
      </div>

      {/* MFA Enrollment Modal */}
      <AnimatePresence>
        {showEnrollment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEnrollment(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <MFAEnrollment
                onComplete={handleEnrollmentComplete}
                onSkip={() => setShowEnrollment(false)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backup Codes Modal */}
      <AnimatePresence>
        {showBackupCodes && backupCodes && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowBackupCodes(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Backup Codes</h3>
                <button
                  onClick={() => setShowBackupCodes(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold mb-1">Save these codes securely!</p>
                  <p>Each code can only be used once. You have {backupCodes.remaining_count} codes.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-6">
                {backupCodes.codes.map((code, index) => (
                  <div
                    key={index}
                    className="p-3 bg-gray-50 rounded-lg font-mono text-sm text-center border border-gray-200"
                  >
                    {code}
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleDownloadBackupCodes}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Codes
                </button>
                <button
                  onClick={handleGenerateBackupCodes}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Regenerate
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disable MFA Modal */}
      <AnimatePresence>
        {showDisableModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDisableModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <Lock className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Disable 2FA</h3>
                  <p className="text-sm text-gray-600">This will reduce your account security</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    TOTP Code or Backup Code
                  </label>
                  <input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    placeholder="000000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    disabled={disabling}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    disabled={disabling}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDisableModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition"
                  disabled={disabling}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisableMFA}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition disabled:opacity-50"
                  disabled={disabling || !disableCode || !disablePassword}
                >
                  {disabling ? 'Disabling...' : 'Disable 2FA'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
